import { IndigoAppContextType } from 'app/AppContext';
import {
    ValueType,
    ColumnDefinitionsDictionary,
} from '@instinet-analytics/semantic-ui-components';
import { buildColDefFromColumnDefinition } from '@instinet-analytics/semantic-ui-components';
import mapValues from 'lodash/mapValues';
import values from 'lodash/values';
import { ColDef } from 'ag-grid-community';
import { pegTypeEnumMap } from 'widgets/openSubmissions/store/stream/pegType';
import { Currency } from 'app/currencies/store/CurrencyState';
import { tickSizeToPrecision } from 'utils/tickSize';

export type OpenSubmissionsColumn =
    | 'executionVenueCode'
    | 'executionVenueName'
    | 'orderQty'
    | 'limitPrice'
    | 'filled'
    | 'updateTimeEpoch'
    | 'pegType';

const GridBorder = 2;
const MinVenueColWidth = 100;

export function openSubmissionsColumnDefinitions(
    orderCurrency?: Currency,
    tickSize?: number
): ColumnDefinitionsDictionary<OpenSubmissionsColumn> {
    return {
        executionVenueCode: {
            field: 'executionVenueCode',
            name: 'VT',
            minWidth: 56,
            width: 56,
            valueType: ValueType.STRING,
            textAlign: 'center',
            cellClass: 'vt',
        },
        executionVenueName: {
            field: 'executionVenueName',
            name: 'Venue',
            valueType: ValueType.STRING,
            textAlign: 'left',
            cellClass: 'venue-cell-body',
            textEllipses: true,
        },
        orderQty: {
            field: 'orderQty',
            name: 'Amount',
            width: 100,
            valueType: ValueType.NUMBER,
        },
        limitPrice: {
            field: 'limitPrice',
            name: `${orderCurrency ? orderCurrency.prefix + ' ' : ''}Price`,
            width: 90,
            precision: tickSizeToPrecision(tickSize),
            numberPrefix: '@',
            valueType: ValueType.NUMBER,
        },
        filled: {
            field: 'filled',
            name: 'Filled',
            width: 63,
            valueType: ValueType.NUMBER,
            isPercentage: true,
        },
        updateTimeEpoch: {
            field: 'updateTimeEpoch',
            name: 'Last Updated',
            width: 112,
            valueType: ValueType.TIME,
        },

        pegType: {
            field: 'pegType',
            name: 'Peg Type',
            width: 120,
            valueType: ValueType.ENUM,
            enumMap: pegTypeEnumMap,
        },
    };
}

export const buildOpenSubmissionsColDefs: (
    context: IndigoAppContextType,
    orderCurrency?: Currency,
    tickSize?: number
) => {
    columnDefinitions: ColumnDefinitionsDictionary<OpenSubmissionsColumn>;
    colDefs: ColDef[];
} = (context, orderCurrency, tickSize) => {
    const columnDefinitions = openSubmissionsColumnDefinitions(
        orderCurrency,
        tickSize
    );
    const colDefs = mapValues(columnDefinitions, (v) =>
        buildColDefFromColumnDefinition(context, v.field, v)
    );

    colDefs.executionVenueCode.cellRenderer = 'venueType';
    colDefs.executionVenueCode.tooltipComponentParams.showAutoCellTooltip =
        false;

    return { columnDefinitions, colDefs: values(colDefs) };
};

export function autoSizeVenueNameColumn(colDefs: ColDef[], width: number) {
    const executionVenueName = colDefs.find(
        (c) => c.colId === 'executionVenueName'
    )!;
    if (!executionVenueName) {
        return;
    }
    if (executionVenueName.width === undefined && width > 0) {
        const colWidths = colDefs
            .map((c) => c.width ?? 0)
            .reduce((t, n) => t + n);
        executionVenueName.width = Math.max(
            MinVenueColWidth,
            width - colWidths - GridBorder
        );
    }
}
